import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Components
import ModalContainer from '../../containers/ModalContainer';
import { MainButton } from '../../../inputs/buttons/MainButton';

// Styled Components
import {
  Container,
  Icon,
  Title,
  Text,
  Version,
  ErrorText,
  ProgressTrack,
  ProgressFill,
} from './styles';

// Types
import { ModalProps } from './types';

// Icons
import RestartIcon from '../../../../assets/icons/restart.svg';
import { useTheme } from 'styled-components';
import RoisAppUpdate from '../../../../plugins/roisAppUpdate';

/**
 * La ÚNICA ventana de actualización de ROIS, para web y para la app.
 *
 * ─── Por qué una sola ──────────────────────────────────────────────────────
 *
 * Había dos sistemas y ninguno sabía del otro: el de la web abría este modal, y
 * el de la app tenía su propia pantalla a plena página. Dentro del APK salía el
 * de la web, que allí no puede hacer nada — recargaba el mismo bundle, así que
 * la versión no cambiaba y el aviso volvía una y otra vez.
 *
 * Ahora el aviso es el mismo en los tres casos y lo que cambia es el motor
 * (ver {@link VersionUpdateMode}). Sustituye también a `MandatoryUpdateScreen`,
 * siguiendo el camino que la PR #178 abrió con el permiso de segundo plano:
 * las situaciones de "hay que hacer algo antes de seguir" se ven todas igual.
 *
 * ─── Sin claves i18n nuevas ────────────────────────────────────────────────
 *
 * Se reutilizan las que ya existen en ca/es/en. La única frase que NO se enseña
 * fuera de la web es `versionUpdate.cacheWarning` ("se limpiará la caché"),
 * porque en la app no se limpia nada: decirlo sería mentir, y encima asusta a un
 * operario que tiene trabajo sin subir.
 */
export const VersionUpdateModal = ({
  onClickClose,
  version,
  reason,
  onContinue,
  isNewVersion = true,
  mode = 'web',
  apkUrl,
  apkChecksum,
  blocking = false,
  onEscape,
  ...props
}: ModalProps): JSX.Element => {
  const { t } = useTranslation(['modals', 'buttons', 'errors', 'common']);
  const theme = useTheme();

  const isApk = mode === 'native-apk';
  const [busy, setBusy] = useState(false);
  const [percent, setPercent] = useState<number | null>(null);
  const [failed, setFailed] = useState(false);
  // Web y bundle: aplicar es recargar. En web el aviso se queda en pantalla
  // hasta que la página se va, así que el botón queda ocupado para que nadie
  // pulse dos veces ni siga trabajando los segundos que tarda.
  const [applying, setApplying] = useState(false);
  // Android concede "instalar apps de esta fuente" app por app. Se pregunta al
  // PULSAR y no al montar: en el 99% de los casos el permiso ya está, y
  // preguntar antes sería un viaje al puente nativo cada vez que sale el aviso.
  const [needsPermission, setNeedsPermission] = useState(false);
  // El instalador de Android reinicia la app, pero el camino de error vuelve
  // aquí — y puede hacerlo cuando el modal ya no está.
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isApk) return;
    let handle: { remove: () => Promise<void> } | null = null;
    void RoisAppUpdate.addListener('downloadProgress', ({ percent: p }) => {
      if (mounted.current) setPercent(p);
    }).then((h) => {
      if (!mounted.current) {
        void h.remove();
        return;
      }
      handle = h;
    });
    return () => {
      if (handle) void handle.remove();
    };
  }, [isApk]);

  // Si el operario fue a Ajustes a conceder el permiso, al volver el botón
  // tiene que dejar de decir «Configuración»: se vuelve a preguntar. Sin esto
  // la etiqueta mentía hasta que se pulsaba otra vez.
  useEffect(() => {
    if (!isApk || !needsPermission) return;
    const onVisible = (): void => {
      if (document.visibilityState !== 'visible') return;
      void RoisAppUpdate.canInstall()
        .then(({ allowed }) => {
          if (allowed && mounted.current) setNeedsPermission(false);
        })
        .catch(() => undefined);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [isApk, needsPermission]);

  const install = useCallback(async () => {
    if (!apkUrl) return;
    setBusy(true);
    setFailed(false);
    try {
      // Antes se descargaban varios MB para tropezar al final con el permiso.
      const { allowed } = await RoisAppUpdate.canInstall();
      if (!allowed) {
        if (mounted.current) setNeedsPermission(true);
        await RoisAppUpdate.openInstallSettings();
        return;
      }
      if (mounted.current) setNeedsPermission(false);
      await RoisAppUpdate.downloadAndInstall({
        url: apkUrl,
        checksum: apkChecksum,
      });
      // Si la instalación sale bien Android reinicia la app: aquí no se llega.
    } catch {
      // Cancelar en el diálogo del sistema, quedarse sin cobertura a media
      // descarga o un checksum que no cuadra terminan aquí. En los tres casos la
      // respuesta es la misma: dejar que lo vuelva a intentar.
      if (mounted.current) {
        setFailed(true);
        setPercent(null);
      }
    } finally {
      if (mounted.current) setBusy(false);
    }
  }, [apkUrl, apkChecksum]);

  // 🔴 Obligatoria + APK + intento fallido = X. Sin cobertura la descarga no
  // puede salir bien por mucho que se reintente, y un aviso sin salida deja la
  // tablet inservible hasta que vuelva la red. No es una forma de aplazar: al
  // ser obligatoria no hay silencio y vuelve en la siguiente comprobación.
  const canEscape = blocking && isApk && failed && !!onEscape;

  /** Qué va a pasar al pulsar el botón, según el motor que haya detrás. */
  const warningPerMode = (): string => {
    if (mode === 'native-apk') return t('modals:versionUpdate.installWarning');
    if (mode === 'native-bundle') return t('modals:versionUpdate.restartWarning');
    return t('modals:versionUpdate.cacheWarning');
  };

  return (
    <ModalContainer
      width={45}
      onClickClose={canEscape ? onEscape : onClickClose}
      // Obligatoria: sin X. `SuperModal` ya ignora el cierre; enseñar el botón
      // solo serviría para que el operario lo pulsara sin efecto. Salvo tras un
      // intento de APK fallido: ahí la X es la salida (ver `canEscape`).
      hideClose={blocking && !canEscape}
      fill={theme.colors.black}
      title={isNewVersion ? t('modals:versionUpdate.title') : t('modals:cacheClear.title')}
    >
      <Container {...props}>
        <Icon src={RestartIcon} />
        {/* Con una versión nueva el contenedor ya lo dice en su título.
            Repetir "Nueva versión detectada" debajo era decir dos veces lo
            mismo. El `reason` sigue haciendo falta en la limpieza manual. */}
        {!isNewVersion && <Title variant="semiBold">{reason}</Title>}
        <Text>
          {isNewVersion && (
            <>
              <strong>
                {t('modals:versionUpdate.version')}: {version}
              </strong>
              <br />
              <br />
            </>
          )}
          {/* Cada camino dice lo que REALMENTE va a pasar. La frase de la
              caché describe el camino web y en la app sería mentira: allí no se
              limpia nada, se reinicia el WebView o se instala un paquete.
              Dejarlo en blanco tampoco valía — el operario pulsa sin saber qué
              le espera, que es justo lo que se vio en la primera prueba. */}
          {isNewVersion
            ? warningPerMode()
            : mode === 'web' && t('modals:cacheClear.description')}
        </Text>
        {isApk && percent !== null && (
          <>
            <ProgressTrack
              role="progressbar"
              aria-valuenow={percent}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <ProgressFill $percent={percent} />
            </ProgressTrack>
            {/* Solo el número: la versión ya está arriba en negrita. */}
            <Version>{percent}%</Version>
          </>
        )}
        {isApk && failed && <ErrorText>{t('errors:generalError')}</ErrorText>}
        <MainButton
          text={
            // El porcentaje va UNA vez, junto a la barra: la barra moviéndose
            // ya dice que está trabajando, repetirlo en el botón sobraba.
            isApk && needsPermission
              ? t('buttons:settings')
              : mode === 'web'
                ? t('common:buttons.continue')
                : t('buttons:update')
          }
          onClick={
            isApk
              ? () => void install()
              : () => {
                  setApplying(true);
                  onContinue();
                }
          }
          disabled={isApk ? busy || !apkUrl : applying}
        />
      </Container>
    </ModalContainer>
  );
};
